import { Box } from '@apia/theme';
import { ControlledMenu, Menu, useMenuState } from '@szhsin/react-menu';
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AiOutlineEllipsis } from '@meronex/icons/ai';
import { IconButton } from '../../../../../common';
import FileIcon from '../../../../../common/FileIcon';
import { FileElementContext } from '../FileElement';
import RenderMenu from './RenderMenu';
import UploaderContext from '../../../../../common/uploader/context';
import { useAppSelector } from '../../../../../../hooks/hooks';
import store from '../../../../../../store';
import { uploaderActions } from '../../../../../../store/fileUploadSlice';
import { isChild } from '../../../../../../utils/dom';
import { getLabel } from '@apia/util';

const MenuUploaderListElement = ({ autoFocus }: { autoFocus?: boolean }) => {
  const { file, onDownloadFile, noDownload } = useContext(FileElementContext);
  const [menuProps, toggleMenu] = useMenuState();
  const [anchorPoint, setAnchorPoint] = useState<null | {
    x: number;
    y: number;
  }>(null);

  const { api } = useContext(UploaderContext);

  const isSelected = useAppSelector((global) =>
    global.fileUploadSlice.uploaders[api.id].selectedFiles.includes(file.docId),
  );
  const selectThisFile = React.useCallback(
    (isCtrlPressed: boolean) => {
      if (isCtrlPressed) {
        store.dispatch(
          uploaderActions.toggleFileSelection({
            fileId: file.docId,
            id: api.id,
          }),
        );
      } else if (!isSelected)
        store.dispatch(
          uploaderActions.selectFile({
            fileId: file.docId,
            id: api.id,
          }),
        );
    },
    [api.id, file.docId, isSelected],
  );
  const ref = React.useRef<HTMLButtonElement>(null);

  const portal = useMemo(
    () => ({
      target: document.querySelector('[data-variant="layout.root"]') as Element,
      stablePosition: true,
    }),
    [],
  );
  const handleOnClose = useCallback(() => toggleMenu(false), [toggleMenu]);
  const title = file.docName || file.name || 'noname';
  const isReadonly = noDownload || (!file.canRead && !file.canEdit);

  const hasAutoFocus = useRef(false);

  return (
    <Box
      className={`uploaderFilesList__withMenuElement ${
        isSelected ? 'selected' : ''
      }`}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorPoint({ x: e.clientX, y: e.clientY });
        toggleMenu(true);
        selectThisFile(e.ctrlKey);
      }}
      data-filename={file.docName || file.name}
      onClick={(ev) => {
        if (
          isChild(ev.target as HTMLElement, (current) =>
            current.classList.contains('fileUploaderMenu'),
          )
        )
          return;
        selectThisFile(ev.ctrlKey);
      }}
      onDoubleClick={(ev) => {
        if (isReadonly) return;
        if (
          !isChild(
            ev.target as HTMLElement,
            (current) =>
              current.classList.contains('fileUploaderMenu') ||
              current.id === 'NonControlledAppModals' ||
              current.id === 'AppModals',
          )
        )
          onDownloadFile(file);
      }}
      tabIndex={0}
      ref={(el) => {
        if (!hasAutoFocus.current && el && autoFocus) {
          hasAutoFocus.current = true;
          el.focus();
        }
      }}
    >
      <Box className="uploaderFilesList__withMenuElement__iconAndLabel">
        <FileIcon fileName={title} />
        <Box
          className="uploaderFilesList__withMenuElement__label"
          title={title}
        >
          {title}
        </Box>
      </Box>
      <Box className="uploaderFilesList__withMenuElement__buttons">
        <IconButton
          disabled={isReadonly}
          className="uploaderFilesList__withMenuElement__optionsButton"
          icon="Download"
          onClick={() => onDownloadFile(file)}
          title={getLabel('btnDow').text}
          aria-label={getLabel('btnDow').text}
        />
        <Menu
          viewScroll="close"
          menuButton={
            <IconButton
              ref={ref}
              className="uploaderFilesList__withMenuElement__optionsButton"
              icon={AiOutlineEllipsis}
              title={getLabel('mnuOpc').text}
              aria-label={getLabel('mnuOpc').text}
            />
          }
          portal={portal}
        >
          <RenderMenu />
        </Menu>
      </Box>
      <ControlledMenu
        {...menuProps}
        anchorPoint={anchorPoint ?? { x: 0, y: 0 }}
        onClose={handleOnClose}
        viewScroll="close"
        portal={portal}
      >
        <RenderMenu />
      </ControlledMenu>
    </Box>
  );
};

export default MenuUploaderListElement;
